import { useCallback, useEffect, useState } from "react";
import { getNodeCapabilities } from "../services/slack";
import { useOptionsClientId } from "./useOptionsClientId";
import { capabilitiesKeyParam, capabilitiesKeyValue } from "../utils/nodeBuilder";

/**
 * Slack capabilities for the node's selected `clientKey`.
 *
 * Driven entirely by `builder.capabilities_endpoint` / `builder.capabilities_key_field`, so other
 * nodes can adopt the same pattern without frontend changes.
 */
export const useNodeCapabilities = ({ definition = null, data = {} } = {}) => {
  const builder = definition?.builder ?? {};
  const endpoint = builder.capabilities_endpoint ?? null;
  // `capabilities_key_field` names the node field, `capabilities_key_param` the query parameter.
  // `default_data` counts as a selected key, matching what the node renders.
  const key = capabilitiesKeyValue(builder, definition, data);
  const keyParam = capabilitiesKeyParam(builder);

  const clientId = useOptionsClientId();

  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!endpoint || !key) {
      setCapabilities(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    setLoading(true);

    getNodeCapabilities({ endpoint, clientId, key, keyParam })
      .then((result) => {
        if (cancelled) return;
        setLoading(false);

        if (result?.success === false) {
          setCapabilities(null);
          setError(result.message || "Could not read the Slack capabilities.");
          return;
        }

        setCapabilities(result);
        setError(null);
      })
      .catch((failure) => {
        if (cancelled) return;
        setLoading(false);
        setCapabilities(null);
        setError(failure?.message || "Could not read the Slack capabilities.");
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, key, keyParam, clientId, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { capabilities, loading, error, reload };
};

export default useNodeCapabilities;
